import React, { useState } from "react";
import { Check, Pencil } from "lucide-react";
import Avatar from "../common/Avatar";
import { playerMetaParts, playerAgentLine } from "./playerRecord";

// B3 — one accepted-player row in the agent's deliver checklist.
//
// Record data only (photo, name, position · age · MV · the player's own agent):
// these are the accepted players on the commissioned request, hydrated from the
// record (Annex Z.7). A checkbox ticks the player into the delivery; an optional
// per-player note ("Add a note for the club") expands inline.
//
// Mobile: the head wraps (checkbox + photo + name on the first line, meta wraps
// to its own line, the agent line below); the note area is full-width; nothing
// shrinks or truncates.
const DeliverPlayerRow = ({ player, checked, note, onToggle, onNoteChange }) => {
  const [noteOpen, setNoteOpen] = useState(!!note);
  const meta = playerMetaParts(player);
  const agentLine = playerAgentLine(player?.player_agent);

  return (
    <div
      className={`border rounded-xl p-3.5 sm:p-4 transition-colors ${
        checked ? "border-[#2283FF] bg-[#F5F9FF]" : "border-[#E5E7EB] bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          aria-label={`Include ${player?.name || "player"} in the delivery`}
          onClick={() => onToggle?.(player.id)}
          className={`mt-0.5 flex-none w-5 h-5 rounded border flex items-center justify-center ${
            checked
              ? "bg-[#2283FF] border-[#2283FF] text-white"
              : "bg-white border-gray-300 hover:border-gray-400"
          }`}
        >
          {checked && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
        </button>

        <Avatar
          src={player?.image}
          fallbackText={player?.name || "?"}
          alt={player?.name || ""}
          size={44}
          className="flex-none"
        />

        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14.5px] font-bold text-[#111827] break-words">
            {player?.name}
          </span>
          {meta.length > 0 && (
            <span className="text-[12.5px] text-[#4B5563] flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5">
              {meta.map((part, i) => (
                <React.Fragment key={part + i}>
                  {i > 0 && <span className="text-[#D1D5DB]">·</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </span>
          )}
          {agentLine && (
            <span className="text-[11.5px] text-gray-500 mt-0.5 break-words">
              <span className="text-gray-400">Player&apos;s agent: </span>
              {agentLine}
            </span>
          )}

          {/* Optional per-player note */}
          {noteOpen ? (
            <textarea
              value={note || ""}
              onChange={(e) => onNoteChange?.(player.id, e.target.value)}
              placeholder="Add a note for the club"
              rows={2}
              className="mt-2.5 w-full text-[13px] border border-[#D6DBE3] rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#2283FF]/30 focus:border-[#2283FF]"
            />
          ) : (
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="mt-2 self-start inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#2283FF] hover:underline"
            >
              <Pencil className="w-3.5 h-3.5" />
              Add a note for the club
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliverPlayerRow;
